import React, { useMemo, memo } from 'react';
import client from '@atlassian/jira-apollo-gira';
import { ApolloClientProvider } from '@atlassian/jira-apollo-multiple-clients';
import { BusinessAppWrapper } from '@atlassian/jira-business-app-wrapper';
import { IssueTypesAndFieldsContext } from '@atlassian/jira-business-entity-project';
import { ExperienceErrorBoundary } from '@atlassian/jira-business-experience-tracking';
import { IssueCreateProvider } from '@atlassian/jira-business-issue-create';
import {
	ContextualAnalyticsData,
	FireScreenAnalytics,
	SCREEN,
} from '@atlassian/jira-product-analytics-bridge';
import { MAIN_EXPERIENCE } from './common/constants';
import { useTimelineFeatures } from './controllers/features-context';
import { useTimelineUI } from './controllers/timeline-ui';
import { CREATE_ISSUE_MUTATION, ISSUE_REFETCH_QUERY } from './services/create-issue';
import TimelineView from './ui/timeline-view';

const analyticsSourceName = 'timelineView';

export const TimelineViewRoot = memo(() => {
	const { hasInlineCreate, hasFilterCustomFields } = useTimelineFeatures();
	const [{ timelineMode }] = useTimelineUI();
	const analyticsAttributes = useMemo(
		() => ({
			timelineMode,
		}),
		[timelineMode],
	);

	const content = (
		<ContextualAnalyticsData
			objectType="timeline"
			attributes={analyticsAttributes}
			sourceType={SCREEN}
			sourceName={analyticsSourceName}
		>
			<TimelineView />
			<FireScreenAnalytics />
		</ContextualAnalyticsData>
	);

	const isUsingIssueTypesAndFields = hasInlineCreate || hasFilterCustomFields;

	return (
		<BusinessAppWrapper withSidebar withOverflow>
			<ApolloClientProvider client={client}>
				{isUsingIssueTypesAndFields ? (
					<IssueTypesAndFieldsContext>
						{hasInlineCreate ? (
							<IssueCreateProvider mutation={CREATE_ISSUE_MUTATION} query={ISSUE_REFETCH_QUERY}>
								{content}
							</IssueCreateProvider>
						) : (
							content
						)}
					</IssueTypesAndFieldsContext>
				) : (
					content
				)}
			</ApolloClientProvider>
		</BusinessAppWrapper>
	);
});

const Timeline = () => (
	<ExperienceErrorBoundary experience={MAIN_EXPERIENCE} shouldStartExperience>
		<TimelineViewRoot />
	</ExperienceErrorBoundary>
);

export default memo(Timeline);
